<template>
  <div>
    <SideBar :sidebar-open="sidebarOpen" @update:sidebar-open="(value) => (sidebarOpen = value)" />

    <div class="xl:pl-72">
      <div
        class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/5 bg-background px-4 shadow-sm sm:px-6 lg:px-8"
      >
        <button type="button" id="sidebar" class="-m-2.5 p-2.5 text-white xl:hidden" @click="sidebarOpen = true">
          <span class="sr-only">{{ t("msg.openSidebar") }}</span>
          <Bars3Icon class="h-5 w-5" aria-hidden="true" />
        </button>

        <SearchBar :model-value="search" @update:model-value="(text) => (search = text)" />
      </div>

      <AdminHeader />

      <main>
        <div class="mx-auto px-12 py-10">
          <div class="flex items-start justify-between">
            <div class="flex flex-col space-y-3">
              <div class="flex items-center gap-3">
                <h1
                  tabindex="-1"
                  class="no-focus-outline scroll-m-20 text-nowrap text-3xl font-bold tracking-tight"
                  ref="initialFocus"
                >
                  {{ t("pages.logs.heading.title") }}
                </h1>
                <a
                  class="ml-2 inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  href="https://docs.knocknoc.io/books/admin-guide/page/logging"
                  target="_blank"
                  :aria-label="`${t('pages.logs.docs')} (${t('common.opensInNewTab')})`"
                >
                  {{ t("common.docs") }}
                  <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                  <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
                </a>
              </div>

              <div class="max-w-2xl">
                <p class="text-base leading-relaxed text-muted-foreground">
                  {{ t("pages.logs.heading.description") }}
                </p>
              </div>
            </div>

            <div class="flex-shrink-0">
              <button
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm ring-offset-background transition-all duration-200 hover:bg-primary/90 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                @click="downloadLogs"
              >
                {{ t("pages.logs.heading.actions.downloadCsv") }}
              </button>
            </div>
          </div>

          <div class="mt-8 flow-root overflow-x-auto">
            <AutoTable
              :columns="[
                { key: 'date', label: t('fields.logs.date') },
                { key: 'username', label: t('fields.logs.username') },
                { key: 'action', label: t('fields.logs.action') },
                { key: 'ip', label: t('fields.logs.ip') },
                {
                  key: 'view',
                  label: t('msg.view'),
                  hidden: true,
                  cellClass: 'relative py-4 pl-3 pr-4 text-right font-medium',
                },
              ]"
              :rows="
                filterData?.map((data) => ({
                  date: DateTime.fromISO(data.Date).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS),
                  username: globals.logEntryActor(data.Entry),
                  action: t(globals.logEntryAction(data.Entry)),
                  ip: Object.values(data?.Entry)?.[0]?.IpAddress || null,
                  view: data,
                }))
              "
            >
              <template #default>
                <TableRow>
                  <TableCell colspan="4" class="text-center text-muted-foreground">
                    <div class="flex items-center justify-center gap-2">
                      <InformationCircleIcon class="h-4 w-4" aria-hidden="true" />
                      <span>
                        {{ t("pages.logs.table.default") }}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              </template>

              <template #col-view="{ data }">
                <a
                  href="#"
                  class="text-[#e05454]"
                  @click="
                    currentLogEntry = data;
                    viewModal = true;
                  "
                >
                  {{ t("msg.view") }}
                </a>
              </template>
            </AutoTable>

            <PageSelect v-model:current-page="currentPage" :total-pages="logChunks.length" />
          </div>
        </div>
      </main>
    </div>
  </div>

  <Dialog :show="viewModal" v-if="currentLogEntry">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>{{ t("msg.logEntry") }}</DialogTitle>

      <div class="max-w-2xl overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-700">
          <tbody class="divide-y divide-gray-800 text-sm">
            <tr>
              <td class="font-semibold">{{ t("fields.logEntry.time") }}</td>
              <td>
                {{ DateTime.fromISO(currentLogEntry.Date).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS) }}
              </td>
            </tr>
            <tr>
              <td class="pr-2 font-semibold">{{ t("fields.logEntry.action") }}</td>
              <td class="pr-2">{{ t(globals.logEntryAction(currentLogEntry.Entry)) }}</td>
            </tr>
            <tr v-for="[key, value] in sortedEntries" :key="key">
              <td class="pr-2 font-semibold">
                {{ t(`fields.logEntry.${key.charAt(0).toLowerCase() + key.slice(1)}`) || key }}
              </td>
              <td class="mr-2">
                <span class="pr-2">{{ value }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <button
        type="button"
        @click="viewModal = false"
        ref="cancelButtonRef"
        class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
      >
        {{ t("common.close") }}
      </button>
    </DialogPanel>
  </Dialog>

  <ErrorNotification :error="errorMessage" :title="t('msg.error')" @update:error="(input) => (errorMessage = input)" />
</template>

<script lang="ts" setup>
import { Bars3Icon } from "@heroicons/vue/20/solid";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { DateTime } from "luxon";

import globals from "../../globals";
import userService from "../../services/user.service.ts";

import SideBar from "../../components/SideBar.vue";
import SearchBar from "../../components/SearchBar.vue";
import ErrorNotification from "../../components/ErrorNotification.vue";

import { Dialog, DialogOverlay, DialogPanel, DialogTitle } from "../../components/ui/dialog";

import { useI18n } from "vue-i18n";
import AutoTable from "../../components/ui/table/AutoTable.vue";
import AdminHeader from "../../components/AdminHeader.vue";
import PageSelect from "../../components/PageSelect.vue";
import { ArrowTopRightOnSquareIcon, InformationCircleIcon } from "@heroicons/vue/24/outline";

const { t } = useI18n();

const errorMessage = ref("");

const search = ref("");
const sidebarOpen = ref(false);

const initialFocus = ref(null);

const logs = ref();
const logChunks = ref([]);

const currentPage = ref(1);
const currentLogEntry = ref(null);
const viewModal = ref(false);

const fieldOrder = [
  "GrantType",
  "UserName",
  "AdminName",
  "AgentName",
  "IpAddress",
  "RequestingIpAddr",
  "ErrorMessage",
  "AclDescription",
  "KnocDescription",
  "Description",
  "GrantTicketNumber",
];

// Computed property to get sorted entries
const sortedEntries = computed(() => {
  console.log(currentLogEntry);
  if (currentLogEntry.value == null) {
    return [];
  }
  const entryData = Object.values(currentLogEntry.value.Entry)[0];
  if (!entryData) return [];

  const entries = Object.entries(entryData).filter(([, value]) => value);

  return entries.sort(([keyA], [keyB]) => {
    const indexA = fieldOrder.indexOf(keyA);
    const indexB = fieldOrder.indexOf(keyB);

    // If both are in fieldOrder, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // If only A is in fieldOrder, it comes first
    if (indexA !== -1) return -1;
    // If only B is in fieldOrder, it comes first
    if (indexB !== -1) return 1;
    // If neither is in fieldOrder, maintain original order
    return 0;
  });
});

const downloadLogs = () => {
  let csv = "data:text/csv;charset=utf-8,";

  let headers = Object.keys(logs.value[0]);
  csv += headers.join(",") + "\n";

  logs.value.forEach(function (row) {
    let data = headers.map((header) => JSON.stringify(row[header])).join(",");
    csv += data + "\n";
  });

  const encoded = encodeURI(csv);
  const link = document.createElement("a");
  link.setAttribute("href", encoded);
  link.setAttribute("download", `${DateTime.now().toISODate()}-knocknoc.csv`);
  document.body.appendChild(link);
  link.click();
};

const filterData = computed(() => {
  if (!search.value) return logChunks.value[currentPage.value - 1];

  return logs.value.filter((item) => {
    for (const key in item) {
      if (typeof item[key] === "string" || item[key] instanceof String) {
        if (item[key].toLowerCase().includes(search.value.toLocaleLowerCase())) {
          return true;
        }
      }
    }
    return false;
  });
});

const fetchData = () => {
  userService.getAuditLogs().then(
    (response) => {
      logs.value = response.data;

      var chunks = [];
      for (var i = 0; i < logs.value.length; i += 25) {
        chunks.push(logs.value.slice(i, i + 25));
      }
      logChunks.value = chunks;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
};

watch(initialFocus, async (el) => {
  if (el) {
    await nextTick();
    el.focus();
  }
});

// Handle escape key globally
const handleEscape = (event: KeyboardEvent) => {
  if (event.key === "Escape" && viewModal.value) {
    viewModal.value = false;
  }
};

onMounted(() => {
  fetchData();
  window.addEventListener("keydown", handleEscape);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleEscape);
});
</script>
